import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import Sidebar from '../../Navigation/Sidebar/Sidebar';
import LoadingSpinner from "../../Loading/component";
import CategoryEditComponent from "./component";

const CategoryEditContainer = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const categoryData = location.state?.categoryData;
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // S3 Base URL
  const S3_BASE_URL = "https://s3.ap-south-1.amazonaws.com/daalchini/dcprod/";

  // Get full image URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${S3_BASE_URL}${imagePath}`;
  };

  // Upload image to S3
  const uploadImageToS3 = async (file) => {
    if (!file) return null;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v2/s3upload/machine-image",
        "POST",
        formDataUpload,
        (response) => {
          const uploadedPath = response?.image_path || response?.data?.image_path || response?.path || response?.url;
          
          if (!uploadedPath) {
            reject(new Error("No image path received from upload API"));
            return;
          }
          
          toastr.success("Success", "Image uploaded successfully");
          resolve(uploadedPath);
        },
        (err) => {
          toastr.error("Failed", "Unable to upload image");
          reject(err);
        }
      );
    });
  };

  useEffect(() => {
    fetchCategoryDetails()
  }, []);

  const fetchCategoryDetails = () => {
    setInitialLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${id}`,
      "GET",
      undefined,
      (response) => {
        if (response) {
          setFormData({
            name: response.name || '',
            description: response.description || '',
          });
          setImagePreview(getFullImageUrl(response.image || response.image_url));
          setImagePath(response.image || response.image_url || '');
        }
        setInitialLoading(false);
      },
      (err) => {
        console.log("Error fetching category:", err);
        toastr.error("Failed", "Unable to fetch category details");
        setInitialLoading(false);
      }
    );
  };

  // Check if form is valid
  const isFormValid = formData.name.trim() !== '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (file) => {
    if (file) {
      if (!file.type.match('image.*')) {
        toastr.error("Error", "Please upload an image file");
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toastr.error("Error", "Image size should be less than 5MB");
        return;
      }
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload image to S3
      setIsImageUploading(true);
      try {
        const uploadedPath = await uploadImageToS3(file);
        setImagePath(uploadedPath);
        setImageFile(file);
        toastr.success("Success", "Image uploaded successfully");
      } catch (error) {
        console.error("Image upload failed:", error);
        setImagePreview(getFullImageUrl(imagePath));
        toastr.error("Error", "Failed to upload image");
      } finally {
        setIsImageUploading(false);
      }
    } else {
      setImagePreview('');
      setImagePath('');
      setImageFile(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toastr.warning("Incomplete Form", "Please enter category name");
      return;
    }
    
    setLoading(true);
    
    // Prepare payload
    const payload = {
      name: formData.name,
      description: formData.description || "",
      image: imagePath || "",
    };

    console.log("Update Category Payload:", payload);
    
    props.getDataFromAPI(
      `/partner/api/v1/product-categories/${id}`,
      'PUT',
      payload,
      (response) => {
        setLoading(false);
        setSuccessMessage(`Category "${formData.name}" has been updated successfully!`);
        setShowSuccessPopup(true);
      },
      (err) => {
        console.log("Error updating category:", err);
        toastr.error("Failed", err?.message || "Unable to update category");
        setLoading(false);
      }
    );
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    // Navigate to categories list page
    navigate('/home/configuration/product-restructure'); // Update this to your actual categories list route
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (initialLoading) {
    return (
      <div className="category-edit-main-container">
        <Sidebar />
        <div className="category-edit-full-page-loader">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <CategoryEditComponent
      formData={formData}
      imagePreview={imagePreview}
      loading={loading || isImageUploading}
      isFormValid={isFormValid}
      onChange={handleChange}
      onImageChange={handleImageChange}
      onSubmit={handleSubmit}
      onBack={handleBack}
      showSuccessPopup={showSuccessPopup}
      successMessage={successMessage}
      onCloseSuccessPopup={handleCloseSuccessPopup}
    />
  );
};

function mapStateToProps(state) {
  return {
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CategoryEditContainer);